/**
 * Table density and text size dropdown selector.
 * Extracted from DocTypeListPageShell.tsx for maintainability.
 */

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Rows3, AlignJustify, List, LayoutList, AArrowUp } from "lucide-react";
import type { TableDensity, TableTextSize } from "@/lib/store/preferencesStore";

const DENSITY_OPTIONS: { value: TableDensity; icon: typeof AlignJustify; label: string }[] = [
  { value: 'compact', icon: AlignJustify, label: 'Compact' },
  { value: 'default', icon: List, label: 'Default' },
  { value: 'comfortable', icon: LayoutList, label: 'Comfortable' },
];

const TEXT_SIZE_OPTIONS: { value: TableTextSize; label: string; desc: string }[] = [
  { value: 'small', label: 'Small', desc: '12px' },
  { value: 'medium', label: 'Medium', desc: '14px' },
  { value: 'large', label: 'Large', desc: '15px' },
];

export function DensityDropdown({
  density, setDensity, textSize, setTextSize,
}: {
  density: TableDensity; setDensity: (d: TableDensity) => void;
  textSize: TableTextSize; setTextSize: (s: TableTextSize) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
          aria-label="Table display"
        >
          <Rows3 className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <div className="px-2 py-1.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Row height</div>
        <DropdownMenuRadioGroup value={density} onValueChange={(v) => setDensity(v as TableDensity)}>
          {DENSITY_OPTIONS.map(({ value, icon: Icon, label }) => (
            <DropdownMenuRadioItem key={value} value={value}>
              <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
              {label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <div className="my-1 border-t border-border" />
        <div className="px-2 py-1.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Font size</div>
        <DropdownMenuRadioGroup value={textSize} onValueChange={(v) => setTextSize(v as TableTextSize)}>
          {TEXT_SIZE_OPTIONS.map(({ value, label, desc }) => (
            <DropdownMenuRadioItem key={value} value={value}>
              <AArrowUp className="mr-2 h-4 w-4 text-muted-foreground" />
              <span>{label}</span>
              <span className="ml-auto text-[11px] text-muted-foreground">{desc}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
